import React, { useMemo } from 'react';

import { SelectedConfiguration } from 'common/types/additional';
import { CardStatus } from 'common/components/CardNav/constants';
import { useActiveSelectedConfiguration } from 'containers/AdditionalDataStorage/context';
import { useGenericFieldsActionsAdopted } from 'containers/FormDataAdapter';

import SecondaryNav from '@danfloss/containers/components/SecondaryNav';

import { OptionType } from 'common/components/Select/types';
import { DanflossProjectFields } from '@danfloss/containers/project-fields';
import { useTranslateFn } from 'utils/useTranslateFn';
import { TranslationKey } from '@danfloss/utils/text-translations';

const SecondaryNavWrapper = () => {
  const { getFieldsByName } = useGenericFieldsActionsAdopted();
  const t = useTranslateFn<TranslationKey>();
  const emptyImageHose = t('variables.hoseNotSelectedImage');
  const defaultImageHose = t('variables.hoseNoImage');
  const emptyImageFitting1 = t('variables.fitting1NotSelectedImage');
  const emptyImageFitting2 = t('variables.fitting2NotSelectedImage');
  const defaultImageFitting = t('variables.fittingNoImage');
  const fields = useMemo(
    () => getFieldsByName([
      DanflossProjectFields.hoseProduct,
      DanflossProjectFields.fitting1Product,
      DanflossProjectFields.fitting2Product,
    ]),
    [getFieldsByName],
  );
  const activeSelectedConfiguration = useActiveSelectedConfiguration();

  const hoseSecondaryNav = useMemo(() => {
    const hoseSelected = fields[DanflossProjectFields.hoseProduct]?.value?.selected as OptionType[] | undefined;

    const descriptionItems: string[] = [];
    const attrs = hoseSelected ? hoseSelected[0].attributes as Record<string, string> : undefined;
    if (attrs?.abrasion_resistance) {
      descriptionItems.push(`Abrasion Resistance: ${attrs?.abrasion_resistance}`);
    }
    if (attrs?.construction) {
      descriptionItems.push(`Construction: ${attrs?.construction}`);
    }
    if (attrs?.impulse_cycle) {
      descriptionItems.push(`Impulse Cycle: ${attrs?.impulse_cycle}`);
    }
    return {
      active: activeSelectedConfiguration === SelectedConfiguration.hose,
      status: hoseSelected ? CardStatus.fulfilled : CardStatus.empty,
      defaultImage: emptyImageHose,
      emptyBodyLabel: !hoseSelected ? 'Hose' : undefined,
      selectedItem: hoseSelected ? {
        image: hoseSelected[0].attributes?.image || defaultImageHose,
        title: hoseSelected[0].title!,
        category: attrs?.category!,
        description: <span>{descriptionItems.map((item) => <p key={item}>{item}</p>)}</span>,
      } : undefined,
    };
  }, [activeSelectedConfiguration, fields[DanflossProjectFields.hoseProduct]?.value?.selected]);

  const fitting1SecondaryNav = useMemo(() => {
    const fitting1Selected = fields[DanflossProjectFields.fitting1Product]?.value?.selected as OptionType[] | undefined;

    const descriptionItems: string[] = [];
    const attrs = fitting1Selected ? fitting1Selected[0].attributes as Record<string, string> : undefined;

    if (attrs?.connection_type) {
      descriptionItems.push(`Connection Type: ${attrs?.connection_type}`);
    }
    if (attrs?.gender) {
      descriptionItems.push(`Gender: ${attrs?.gender}`);
    }
    if (attrs?.threads) {
      descriptionItems.push(`Threads: ${attrs?.threads}`);
    }

    return {
      active: activeSelectedConfiguration === SelectedConfiguration.fitting1,
      status: fitting1Selected ? CardStatus.fulfilled : CardStatus.empty,
      defaultImage: emptyImageFitting1,
      emptyBodyLabel: !fitting1Selected ? 'Fitting 1' : undefined,
      selectedItem: fitting1Selected ? {
        image: fitting1Selected[0].attributes?.image || defaultImageFitting,
        title: fitting1Selected[0].title!,
        category: attrs?.category!,
        description: <span>{descriptionItems.map((item) => <p key={item}>{item}</p>)}</span>,
      } : undefined,
    };
  }, [activeSelectedConfiguration, fields.fitting1Product]);

  const fitting2SecondaryNav = useMemo(() => {
    const fitting2Selected = fields[DanflossProjectFields.fitting2Product]?.value?.selected as OptionType[] | undefined;

    const descriptionItems: string[] = [];
    const attrs = fitting2Selected ? fitting2Selected[0].attributes as Record<string, string> : undefined;

    if (attrs?.connection_type) {
      descriptionItems.push(`Connection Type: ${attrs?.connection_type}`);
    }
    if (attrs?.gender) {
      descriptionItems.push(`Gender: ${attrs?.gender}`);
    }
    if (attrs?.threads) {
      descriptionItems.push(`Threads: ${attrs?.threads}`);
    }

    return {
      active: activeSelectedConfiguration === SelectedConfiguration.fitting2,
      status: fitting2Selected ? CardStatus.fulfilled : CardStatus.empty,
      defaultImage: emptyImageFitting2,
      emptyBodyLabel: !fitting2Selected ? 'Fitting 2' : undefined,
      selectedItem: fitting2Selected ? {
        image: fitting2Selected[0].attributes?.image! || defaultImageFitting,
        title: fitting2Selected[0].title!,
        category: attrs?.category!,
        description: <span>{descriptionItems.map((item) => <p key={item}>{item}</p>)}</span>,
      } : undefined,
    };
  }, [activeSelectedConfiguration, fields.fitting2Product]);

  return (
    <SecondaryNav
      hose={hoseSecondaryNav}
      fitting2={fitting2SecondaryNav}
      fitting1={fitting1SecondaryNav}
    />
  );
};

export default SecondaryNavWrapper;
